import React, { forwardRef, useCallback, useMemo } from 'react';
import {
    useTheme,
    experimentalStyled as styled,
  } from '@material-ui/core/styles'
import { Stack, Grid, Box, Typography } from '@material-ui/core';
import { FacilityTypes, UNITS } from 'src/constants/Defaultvalues';
import useCompanyProfile from 'src/hooks/useCompanyProfile';
import moment from 'moment';
import { formatDateOnly } from 'src/utils/dateOnly';
import { empty } from 'src/Helpers/Common';
import { fUSPrice } from 'src/utils/formatNumber';
import { getCustomerOrderProductGroupDisplayName, getGroupInstallNotesDisplay } from 'src/pages/dashboard/quotation/quotationGroupingHelpers';
import { getInvoiceProductDisplayName, buildGroupedDesignOrderDisplayRows } from 'src/utils/designOrderInvoiceUtils';
import { isRushFeeOn, calcRushFeeAmount, calcTotalWithRushFee } from 'src/utils/rushFee';

const SalesOrderInvoicePage = forwardRef(({ invoiceDate, details, additionalSelectedServices, invoice, subTotal, cartService, openInvoice = true, invoiceSelectedServices, data, userType, labourInstallService, isFromCompletedTab = false, hasViewCostPermission = true, taxRateData = [], taxAmount = 0, customerOrderProductGroups = [], markupType, groupProductMarketingValues = {}, groupInstallMarketingValues = {}, rushFeeEnabled, rushFeeAmount: rushFeeAmountProp, rushFeePercentage }, ref) => {
    const getProductGroupId = (product) =>
      product?.group_id ?? product?.customer_order_product_group_id ?? product?.groupId ?? product?.groupMeta?.id ?? null

    const getProductLineTotalExcludingInstall = (val) => {
      const { product_retail_price, actual_retail_price, adjustment, quantity, locationQuantity, markAdjType, product_total, material } = val
      let SPMA = 0
      if (markAdjType == 2 || markAdjType == 3) {
        SPMA = (actual_retail_price || product_retail_price || 0) + (adjustment ? Number(adjustment) : 0)
      } else {
        SPMA = (actual_retail_price || product_retail_price || 0) + (((product_retail_price || 0) * (adjustment ? Number(adjustment) : 0)) / 100)
      }
      if (markAdjType == 1 && material === 0) return product_total || 0
      if (material === 1) return SPMA * locationQuantity
      return SPMA * (locationQuantity * quantity)
    }

    const getProductDisplayTotal = (val) => {
      const lineTotal = getProductLineTotalExcludingInstall(val)
      const installTotal = (Number(val?.install_cost) || 0) * (Number(val?.install_qty ?? 1) || 1)
      return lineTotal + installTotal
    }

    const groupedInvoiceDisplayRows = useMemo(() => {
      const rows = buildGroupedDesignOrderDisplayRows({
        designOrderProducts: invoice?.cust_product || [],
        customerOrderProductGroups,
        markupType: Number(markupType ?? details?.markup_type) || 2,
        groupProductMarketingValues,
        groupInstallMarketingValues,
      })
      const isMaterialRow = (row) =>
        row?.__type === 'group'
          ? Number(row?.representative?.material) === 1
          : Number(row?.material) === 1
      return [...rows].sort((a, b) => Number(isMaterialRow(a)) - Number(isMaterialRow(b)))
    }, [
      invoice?.cust_product,
      customerOrderProductGroups,
      markupType,
      details?.markup_type,
      groupProductMarketingValues,
      groupInstallMarketingValues,
    ])

    const groupedTandMDisplayRows = useMemo(
      () =>
        buildGroupedDesignOrderDisplayRows({
          designOrderProducts: (invoice?.cust_product || []).filter((item) => item.material != 1),
          customerOrderProductGroups,
          markupType: Number(markupType ?? details?.markup_type) || 2,
          groupProductMarketingValues,
          groupInstallMarketingValues,
        }),
      [
        invoice?.cust_product,
        customerOrderProductGroups,
        markupType,
        details?.markup_type,
        groupProductMarketingValues,
        groupInstallMarketingValues,
      ],
    )

    // NEW: Same Product Total math as Invoice Preview modal — use product_total for wholesale,
    // apply adjustments like line rows (old version ignored both and undercounted Subtotal)
    const getInvoicePageNonGroupProductTotal = useCallback((val) => {
      const {
        markAdjType,
        material = 0,
        adjustment = 0,
        // REMOVED: adj_value_type — only used by old material path
        // adj_value_type,
        actual_retail_price,
        product_retail_price,
        product_total, // NEW: for markAdjType 1 (wholesale) stored line total
        locationQuantity = 1,
        quantity = 1,
      } = val || {}
      // NEW: Wholesale — use stored product_total (matches backend / line totals)
      if (Number(markAdjType) === 1 && Number(material) !== 1) {
        return Number(product_total) || 0
      }
      // actual_retail_price already includes marketing adjustment (same as ViewOfficialRequest form).
      // Do not add `adjustment` again — that double-counts and inflates PRODUCT TOTAL.
      let basePriceForUnit = Number(actual_retail_price) || Number(product_retail_price) || 0;
      let SPMA = basePriceForUnit;
      if (Number(material) === 1) {
        SPMA = SPMA + (Number(val.adj_value_type) === 1 ? Number(adjustment || 0) : -Number(adjustment || 0));
        return Number(SPMA) * (Number(locationQuantity) || 1)
      }
      return Number(SPMA) * ((Number(locationQuantity) || 1) * (Number(quantity) || 1))
      // const basePrice =
      //   actual_retail_price !== 0 && actual_retail_price != null
      //     ? actual_retail_price
      //     : product_retail_price
      // let SPMA = markAdjType === 2 || markAdjType === 3 ? basePrice : basePrice
      // if (material === 1) {
      //   SPMA = Number(SPMA) + (Number(adj_value_type) === 1 ? Number(adjustment) : -Number(adjustment))
      // }
      // if (markAdjType === 1 && material === 0) {
      //   return SPMA * (locationQuantity * quantity)
      // }
      // return material === 1 ? SPMA * locationQuantity : SPMA * (locationQuantity * quantity)
    }, [])

    // NEW: Prefer stored install_total; fall back to install_cost * qty
    const getInvoicePageNonGroupInstallTotal = useCallback((val) => {
      if (!val) return 0
      if (Number(val?.install_qty) === 0) return 0
      const installTotalLine = parseFloat(val?.install_total || 0)
      if (installTotalLine) return installTotalLine
      const installCost = parseFloat(val?.install_cost || 0)
      const qty = parseFloat(val?.install_qty ?? val?.install_quantity ?? 1) || 1
      if (installCost) return installCost * qty
      return 0
    }, [])

    const invoicePageProductTotalFromRows = useMemo(
      () =>
        (groupedInvoiceDisplayRows || []).reduce((sum, row) => {
          if (row?.__type === 'group') {
             // FIX: Sum member line totals AND add back the group marketing adjustment
            const members = row?.members || []
            const membersSum = members.reduce((s, m) => s + getInvoicePageNonGroupProductTotal(m), 0)

            const productMarketing = groupProductMarketingValues?.[row.groupId] || {}
            const groupAdjustment = Number(
              productMarketing.adjustment ?? row?.groupMeta?.product_marketing_adjustment ?? row?.groupMeta?.productMarketingAdjustment ?? 0
            )
            const groupAdjValueType = 
              productMarketing.adjValueType ?? row?.groupMeta?.product_marketing_adj_value_type ?? row?.groupMeta?.productMarketingAdjValueType ?? row?.representative?.adj_value_type ?? true
            const productSignedAdj = (groupAdjValueType === false || Number(groupAdjValueType) === 0) ? -groupAdjustment : groupAdjustment;
            const groupMarketingDelta = productSignedAdj * Number(row?.groupProductDisplayQty ?? 1);

            return sum + membersSum + groupMarketingDelta;
            // const groupHasInstall = Boolean(row?.groupHasInstall)
            // return (
            //   sum +
            //   (groupHasInstall
            //     ? Number(row?.groupProductOnlyTotal ?? 0)
            //     : Number(row?.totalValue ?? 0))
            // )
          }
          return sum + getInvoicePageNonGroupProductTotal(row)
        }, 0),
      [groupedInvoiceDisplayRows, getInvoicePageNonGroupProductTotal, groupProductMarketingValues],
    )

    const invoicePageInstallTotalFromRows = useMemo(
      () =>
        (groupedInvoiceDisplayRows || []).reduce((sum, row) => {
          if (row?.__type === 'group') {
            // FIX: Sum member install totals AND add back the group marketing adjustment
            const members = row?.members || []
            const membersSum = members.reduce((s, m) => s + getInvoicePageNonGroupInstallTotal(m), 0)

            const installMarketing = groupInstallMarketingValues?.[row.groupId] || {}
            const groupInstallAdjustment = Number(
              installMarketing.adjustment ?? row?.groupMeta?.install_marketing_adjustment ?? row?.groupMeta?.installMarketingAdjustment ?? 0
            )
            const groupInstallAdjValueType = 
              installMarketing.adjValueType ?? row?.groupMeta?.install_marketing_adj_value_type ?? row?.groupMeta?.installMarketingAdjValueType ?? row?.representative?.install_adj_value_type ?? true
            const installSignedAdj = (groupInstallAdjValueType === false || Number(groupInstallAdjValueType) === 0) ? -groupInstallAdjustment : groupInstallAdjustment;
            const groupInstallMarketingDelta = installSignedAdj * Number(row?.groupInstallDisplayQty ?? 1);

            return sum + membersSum + groupInstallMarketingDelta;
          }
          return sum + getInvoicePageNonGroupInstallTotal(row)
        }, 0),
      [groupedInvoiceDisplayRows, getInvoicePageNonGroupInstallTotal, groupInstallMarketingValues],
    )

    const invoicePageCustProductSubtotalForServices = useMemo(
      () => invoicePageProductTotalFromRows + invoicePageInstallTotalFromRows,
      [invoicePageProductTotalFromRows, invoicePageInstallTotalFromRows],
    )

    // Footer Total must match Subtotal + tax - discount (same math as the Subtotal row).
    // Do not use stale invoice.grandTotal — combined orders often had child tax/addl baked in.
    const invoicePageFooterSubtotal = useMemo(() => {
      const productOrTm = Number(invoice?.project_category) === 12
        ? (Number(subTotal) || 0)
        : (Number(invoicePageCustProductSubtotalForServices) || 0)
      const additionalServices = (additionalSelectedServices || []).reduce(
        (s, x) => s + (Number(x.total_cost) || 0),
        0,
      )
      const cartServices = (cartService || []).reduce((s, x) => {
        const sp = Number(x.sellingPrice || x.unit_cost || 0)
        const markup = Number(x.originalMarkup || x.product_markup || 0)
        const final = sp + (sp * markup / 100)
        return s + ((Number(x.qty) || 0) * final)
      }, 0)
      return productOrTm + additionalServices + cartServices
    }, [
      invoice?.project_category,
      subTotal,
      invoicePageCustProductSubtotalForServices,
      additionalSelectedServices,
      cartService,
    ])

    const invoicePageFooterTotal = useMemo(() => {
      const discount = Number(invoice?.extra_discount) || 0
      const tax = Number(invoice?.tax_amount) || 0
      const total = parseFloat((invoicePageFooterSubtotal - discount + tax).toFixed(2))
      // Match ViewOfficialRequest: No Charge (project_category 13) shows negative total
      const isNoCharge =
        Number(invoice?.project_category) === 13 ||
        Number(details?.project_category) === 13 ||
        details?.job_category_name === 'No Charge'
      return isNoCharge && total > 0 ? -total : total
    }, [
      invoicePageFooterSubtotal,
      invoice?.extra_discount,
      invoice?.tax_amount,
      invoice?.project_category,
      details?.project_category,
      details?.job_category_name,
    ])

    const canViewCosts = !!hasViewCostPermission;
    // Same subtotal used by the SUBTOTAL row on this page
    const invoicePageSubtotalForRush =
      (Number(invoice?.project_category) === 12 ? (subTotal || 0) : (invoicePageCustProductSubtotalForServices || 0)) +
      (invoiceSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
      (additionalSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
      (cartService?.reduce((s, x) => {
        const sp = x.sellingPrice || x.unit_cost || 0;
        const markup = x.originalMarkup || x.product_markup || 0;
        const final = sp + (sp * markup) / 100;
        return s + ((x.qty || 0) * final);
      }, 0) || 0);

    const rushFeePct =
      Number(rushFeePercentage) ||
      Number(data?.rush_fee_percentage) ||
      0;

    const showRushFee = isRushFeeOn({
      rushFee: details?.rush_fee,
      rushFeeAmount: rushFeeAmountProp || details?.rush_fee_amount,
      rushFeeEnabled,
    });

    const rushFeeAmount = calcRushFeeAmount({
      enabled: showRushFee,
      subtotal: invoicePageSubtotalForRush,
      percentage: rushFeePct,
      storedAmount: rushFeeAmountProp || details?.rush_fee_amount,
      preferStored: true,
    });

    const invoicePageDisplayTotalRaw = calcTotalWithRushFee({
      subtotal: invoicePageSubtotalForRush,
      discount: invoice?.extra_discount,
      tax: invoice?.tax_amount,
      rushFee: rushFeeAmount,
    });
    // Match ViewOfficialRequest negativeCheck: No Charge totals display as negative
    const isNoChargeJob =
      Number(invoice?.project_category) === 13 ||
      Number(details?.project_category) === 13 ||
      details?.job_category_name === 'No Charge'
    const invoicePageDisplayTotal =
      isNoChargeJob && invoicePageDisplayTotalRaw > 0
        ? -invoicePageDisplayTotalRaw
        : invoicePageDisplayTotalRaw
    const renderRushFeeLabelCell = () => (
      <td
        style={{
          border: '1px solid black',
          padding: '12px',
          textAlign: 'center',
          backgroundColor: '#d9d9d9',
          fontWeight: 'bold',
          color: 'black',
          whiteSpace: 'normal',
          wordBreak: 'keep-all',
          overflowWrap: 'normal',
          hyphens: 'none',
          fontSize: '12px',
          lineHeight: '14px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>RUSH</span>
          <span style={{ whiteSpace: 'nowrap' }}>FEE</span>
        </div>
      </td>
    );
    const formattedNotes = Array.isArray(details?.notes_lines) && details.notes_lines.length
    ? details.notes_lines.join('\n')  // Join with a newline for line breaks
    : '';
    const billToAddress = details?.bill_to || '';
    const shipToAddress = details?.ship_to || '';
    const theme = useTheme();
    // const { COMPANY_LOGO, COMPANY_NAME } = useCompanyProfile()
    const { COMPANY_LOGO } = useCompanyProfile()
    const showInstallPriceInPreview = details?.show_install_price === 1;
    let customerProductTotal = 0;
    return (
        <>
            <Box
                component="style"
                dangerouslySetInnerHTML={{
                    __html: `
                      @media print {
                        html, body, #root {
                          background: #ffffff !important;
                        }
                      }
                    `
                }}
            />
            <Stack ref={ref} style={{ background: '#ffffff', color: '#000000' }}>
            <Grid container spacing={1} sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 510, paddingRight: '6px' }}>
                            {/* <Box component="img" alt="logo" src={COMPANY_LOGO} id="logoImage" sx={{ height: 98, alignSelf: 'flex-start', filter: 'grayscale(100%)' }} /> */}
                            <Box component="img" alt="logo" src={COMPANY_LOGO} sx={{ alignSelf: 'flex-start', filter: 'grayscale(100%)', width: "250px" }} />
                            <Stack direction="column" sx={{ mt: 1, color: 'black', textTransform: 'uppercase',}}>
                                {/* <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{COMPANY_NAME}</Typography>
                                <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.company_website}</Typography> */}
                                <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.address}</Typography>
                                <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.city}, {data?.state}, {data?.zipcode}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                    {/* <Typography variant="h6" sx={{ textTransform: 'uppercase', }}>{(data?.phone_country_code || data?.phone) ? `+${data?.phone_country_code}${data?.phone}` : '951-682-9660'}</Typography>
                                    <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>|</Typography>
                                    <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.company_fax_number ? `Fax: ${data?.company_fax_number}` : ''}</Typography> */}
                                </Box>
                                {/* <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>{data?.company_mail}</Typography> */}
                            </Stack>
                        </Box>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 516, }}>
                        <Typography
                            variant="h3"
                            sx={{
                                textTransform: 'uppercase',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: 'black',
                                mb: 1,
                                fontSize: '1.75rem'
                            }}
                        >
                            {isFromCompletedTab || details?.installation_acceptance == 1 ? 'Invoice' : 'Sales Order'}
                        </Typography>
                        <Box
                            component="style"
                            dangerouslySetInnerHTML={{
                                __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                            }}
                        />
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                border: '1px solid black',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase',
                                tableLayout: 'fixed'
                            }}
                        >
                            <colgroup>
                                <col style={{ width: '50%' }} />
                                <col style={{ width: '50%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? 'Date' : 'Date'}
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? 'Invoice #' : details?.allInstallationCompleted ? "Sales Order Id #" : 'ID #'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {invoiceDate}
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        #{details?.doc_number || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                borderBottom: '1px solid black',
                                borderLeft: '1px solid black',
                                borderRight: '1px solid black',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase',
                                marginTop: 0,
                                tableLayout: 'fixed'
                            }}
                        >
                            <colgroup>
                                <col style={{ width: '50%' }} />
                                <col style={{ width: '50%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        PO #
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? 'PRIMARY SALES REP' : 'Sales Rep'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {/* {details?.community_po_number
                                            ? details.community_po_number
                                                .split(',')
                                                .map((s) => s.trim())
                                                .filter(Boolean)
                                                .map((s) => (s.startsWith('#') ? s : `#${s}`))
                                                .join(', ')
                                            : ''} */}
                                            {invoice?.po_number || ''}
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {details?.sales_rep_names || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                borderBottom: '1px solid black',
                                borderTop: '1px solid black',
                                borderLeft: '1px solid black',
                                borderRight: '1px solid black',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase',
                                marginTop: 0,
                                marginBottom: '10px',
                                tableLayout: 'fixed'
                            }}
                        >
                            <colgroup>
                                <col style={{ width: '50%' }} />
                                <col style={{ width: '50%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            borderBottom: '1px solid black',
                                            borderLeft: '1px solid black',
                                            borderRight: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        PAYMENT TERMS
                                    </th>
                                    <th
                                        style={{
                                            borderBottom: '1px solid black',
                                            borderLeft: '1px solid black',
                                            borderRight: '1px solid black',
                                            backgroundColor: '#ffffff',
                                            padding: '12px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {canViewCosts
                                            ? `${invoice?.customer_billing_type} ( ${invoice?.customer_billing_type_value} % )`
                                            : '—'}
                                    </th>
                                </tr>
                            </thead>
                        </table>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={1} sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', mt: 0.2 }}>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: -10 }}>
                    <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 510, paddingRight: '6px' }}>
                        <Box
                            component="style"
                            dangerouslySetInnerHTML={{
                                __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                            }}
                        />
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                border: '1px solid black',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase'
                            }}
                        >
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        CLIENT NAME AND ADDRESS (BILL TO:)
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {billToAddress ? (
                                            <Typography component="span" sx={{ whiteSpace: 'pre-line' }}>
                                                {billToAddress}
                                            </Typography>
                                        ) : (
                                            <>
                                                {
                                                    details?.is_split_order && details?.for_parent_facility == 1
                                                        ? FacilityTypes?.find(facility => facility.id === details?.origin_facility_id)?.label || invoice?.customer_name
                                                        : invoice?.customer_name
                                                }
                                                <br />
                                                {invoice?.customerAddress || ''}
                                                <br />
                                                {invoice?.customer_city || ''} {invoice?.customer_state || ''} {invoice?.customer_zipcode || ''}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 516 }}>
                        <Box
                            component="style"
                            dangerouslySetInnerHTML={{
                                __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                            }}
                        />
                        
                        <>
                        {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                borderBottom: '1px solid black',
                                borderLeft: '1px solid black',
                                borderRight: '1px solid black',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase',
                                marginTop: 0,
                                tableLayout: 'fixed'
                            }}
                        >
                            <colgroup>
                                <col style={{ width: '50%' }} />
                                <col style={{ width: '50%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? 'CLIENT CONTACT NAME' : 'PROJECT CONTACT NAME'}
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? 'CLIENT PHONE NUMBER' : 'PROJECT PHONE NUMBER'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? invoice?.customer_contact_name : details?.community_name || ''}
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {isFromCompletedTab || details?.installation_acceptance == 1 ? invoice?.customer_phone : details?.community_phone || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        </>
                        

                        {/* PROJECT INSTALL ADDRESS */}
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                borderLeft: '1px solid black',
                                borderRight: '1px solid black',
                                borderBottom: '1px solid black',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase',
                                marginTop: 0
                            }}
                        >
                            <thead>
                                <tr>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        PROJECT INSTALL ADDRESS
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {shipToAddress ? (
                                            <Typography component="span" sx={{ whiteSpace: 'pre-line' }}>
                                                {shipToAddress}
                                            </Typography>
                                        ) : (
                                            <>
                                                {details?.community_address || ''}
                                                <br />
                                                {details?.community_city || ''} {details?.community_state || ''} {details?.community_zipcode || ''}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Grid>
            </Grid>

            <Grid container sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                        {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                        <table
                            className="sales-order-table"
                            style={{
                                width: '100%',
                                border: 'none',
                                borderCollapse: 'collapse',
                                textTransform: 'uppercase',
                                tableLayout: 'fixed'
                            }}
                        >
                            <colgroup>
                                <col style={{ width: '4%' }} />
                                <col style={{ width: '42%' }} />
                                <col style={{ width: '14%' }} /> {/* SKU */}
                                <col style={{ width: '8%' }} />  {/* UNIT (blank) */}
                                <col style={{ width: '12%' }} />  {/* QTY */}
                                <col style={{ width: '10%' }} /> {/* UNIT PRICE */}
                                <col style={{ width: '10%' }} /> {/* TOTAL */}
                            </colgroup>
                            <thead>
                                <tr>
                                <th
                                        colSpan={1}
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        &nbsp;
                                    </th>
                                    <th
                                        colSpan={1}
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        NOTES
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        PROJECT NAME
                                    </th>
                                    <th
                                        colSpan={3}
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        JOB NAME
                                    </th>
                                    <th
                                        style={{
                                            border: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: '#d9d9d9',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        DUE DATE
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                <td
                                    style={{
                                        borderTop: '1px solid black',
                                        borderLeft: '1px solid black',
                                        borderRight: '1px solid black',
                                        padding: '12px',
                                        textAlign: 'center',
                                        backgroundColor: 'white',
                                        color: 'black',
                                        textTransform: 'uppercase',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                        lineHeight: '18px'
                                    }}
                                >
                                    &nbsp;
                                </td>
                                    <td
                                        colSpan={1}
                                            style={{
                                                borderTop: '1px solid black',
                                                borderLeft: '1px solid black',
                                                borderRight: '1px solid black',
                                                padding: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: 'black',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'pre-line',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                lineHeight: '18px'
                                            }}
                                        >
                                        {formattedNotes}
                                    </td>
                                    <td
                                        style={{
                                            borderTop: '1px solid black',
                                            borderLeft: '1px solid black',
                                            borderRight: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            lineHeight: '18px'
                                        }}
                                    >
                                        {details?.community_name || ''}
                                    </td>
                                    <td
                                        colSpan={3}
                                        style={{
                                            borderTop: '1px solid black',
                                            borderLeft: '1px solid black',
                                            borderRight: '1px solid black',
                                            padding: '12px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            textTransform: 'uppercase',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            lineHeight: '18px'
                                        }}
                                    >
                                        {details?.name || ''}
                                    </td>
                                    <td
                                        style={{
                                            borderTop: '1px solid black',
                                            borderLeft: '1px solid black',
                                            borderRight: '1px solid black',
                                            padding: '10px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            color: 'black',
                                            fontSize: '14px',
                                            textTransform: 'uppercase',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            lineHeight: '18px'
                                        }}
                                    >
                                        {formatDateOnly(details?.participation_ends || details?.installation_end_date)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Grid>
                {(details?.job_category_name == 'Time & Material') ?
                    <>
                        {(invoice?.cust_product?.filter(item => item.material != 1)?.length > 0) ? <>
                            <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                    <Box
                                        component="style"
                                        dangerouslySetInnerHTML={{
                                            __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                        }}
                                    />
                                    {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                    <table
                                        className="sales-order-table"
                                        style={{
                                            width: '100%',
                                            border: '1px solid black',
                                            borderCollapse: 'collapse',
                                            textTransform: 'uppercase',
                                            tableLayout: 'fixed'
                                        }}
                                    >
                                        <colgroup>
                                            <col style={{ width: '4%' }} />
                                            <col style={{ width: '40%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '8%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '10%' }} />
                                            <col style={{ width: '10%' }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    #
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    PRODUCTS DESCRIPTION
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    SKU
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    QTY 
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    UNIT PRICE
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    TOTAL
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupedTandMDisplayRows?.map((row, index) => {
                                                if (row?.__type === 'group') {
                                                    const representative = row?.representative || {}
                                                    const groupName = getCustomerOrderProductGroupDisplayName({
                                                        groupMeta: row?.groupMeta,
                                                        representative,
                                                        members: row?.members,
                                                        groupId: row?.groupId,
                                                    })
                                                    const members = row?.members || []
                                                    const groupDisplayName = groupName
                                                    const groupDisplayNote =
                                                        members.find(
                                                            (m) =>
                                                                m?.product_notes != null &&
                                                                String(m.product_notes).trim() !== '',
                                                        )?.product_notes || ''
                                                    const groupProductDisplayQty = Number(row?.groupProductDisplayQty ?? 1) || 1
                                                    const displayGroupTotal = Number(row?.groupProductOnlyTotal ?? 0)
                                                    const unitEach = Number(row?.adjustedGroupRetailEach ?? 0) || (groupProductDisplayQty > 0 ? displayGroupTotal / groupProductDisplayQty : 0)

                                                    return (
                                                        <tr key={`group-${row?.groupId}`}>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {index + 1}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: groupDisplayNote ? 'error.main' : 'inherit' }}>
                                                                    {groupDisplayNote || groupDisplayName}
                                                                </Typography>
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                -
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {canViewCosts ? `$${unitEach.toFixed(2)}` : '—'}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {groupProductDisplayQty}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {canViewCosts ? `$${displayGroupTotal.toFixed(2)}` : '—'}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                                const val = row;
                                                const { vid, product_name, product_notes, product_price, product_retail_price, adjustment,
                                                    quantity, locationQuantity, actual_retail_price, install_cost, install_notes, product_total, discount, unit, unit_qty, material, rfp, markAdjType,
                                                    services = [],
                                                } = val

                                                let SPMA = 0
                                                if (markAdjType == 2 || markAdjType == 3) {
                                                    SPMA = (actual_retail_price || product_retail_price || 0) + (adjustment ? Number(adjustment) : 0)
                                                } else {
                                                    SPMA = (actual_retail_price || product_retail_price || 0) + (((product_retail_price || 0) * (adjustment ? Number(adjustment) : 0)) / 100)
                                                }
                                                SPMA = parseFloat(SPMA)?.toFixed(2)

                                                let rowTotal = 0;
                                                if (markAdjType == 1) {
                                                    rowTotal = product_total || 0.00;
                                                } else {
                                                    rowTotal = SPMA * (locationQuantity * quantity);
                                                }
                                                return (
                                                    <React.Fragment key={vid}>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {index + 1}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{product_name}</Typography>
                                                                {product_notes && (
                                                                    <Typography variant="body2" sx={{ color: 'red', textDecoration: 'underline' }}>Note: {product_notes}</Typography>
                                                                )}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {val?.vpt_number || '-'}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {canViewCosts ? `$${(Number(actual_retail_price) || Number(SPMA) || 0).toFixed(2)}` : '—'}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {locationQuantity}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {canViewCosts ? `$${Number(rowTotal).toFixed(2)}` : '—'}
                                                            </td>
                                                        </tr>
                                                        {install_cost > 0 && showInstallPriceInPreview && (
                                                            <tr>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                    <Typography sx={{ color: install_notes ? 'red' : 'inherit', whiteSpace: 'pre-wrap' }}>
                                                                        {install_notes || 'Install Price'}
                                                                    </Typography>
                                                                </td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                    {canViewCosts ? `$${Number(install_cost).toFixed(2)}` : '—'}
                                                                </td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                    {canViewCosts ? `$${(Number(install_cost) * locationQuantity * quantity).toFixed(2)}` : '—'}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </Box>
                            </Grid>
                        </> : null}
                        {(!empty(invoice?.jobcost_product)) ?
                            <>
                                <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                        <Box
                                            component="style"
                                            dangerouslySetInnerHTML={{
                                                __html: `
                .sales-order-table th {
                  background-color: #d9d9d9 !important;
                  color: black !important;
                }
              `
                                            }}
                                        />
                                        {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                        <table
                                            className="sales-order-table"
                                            style={{
                                                width: '100%',
                                                border: '1px solid black',
                                                borderCollapse: 'collapse',
                                                textTransform: 'uppercase',
                                                tableLayout: 'fixed'
                                            }}
                                        >
                                            <colgroup>
                                                <col style={{ width: '4%' }} />
                                                <col style={{ width: '42%' }} />
                                                <col style={{ width: '14%' }} />
                                                <col style={{ width: '8%' }} />
                                                <col style={{ width: '12%' }} />
                                                <col style={{ width: '10%' }} />
                                                <col style={{ width: '10%' }} />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        #
                                                    </th>

                                                    <th
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >NAME</th>
                                                    <th style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-all',
                                                        overflowWrap: 'break-word',
                                                        hyphens: 'auto',
                                                        lineHeight: '18px'
                                                    }}>QTY</th>
                                                    <th style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>RATE</th>
                                                    <th 
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>TOTAL COST</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoice?.jobcost_product?.map((val, index) => {
                                                    const { id, product_name, unit_price, quantity, total, unit, unit_qty, partial_unit } = val
                                                    let unitToUse = partial_unit ? partial_unit : unit
                                                    let unitName = UNITS?.filter((item) => item?.id == unitToUse)
                                                    let shortUnitName = unitName[0]?.short_name || 'Unit'
                                                    unitName = unitName[0]?.name
                                                    return (
                                                        <tr key={index}>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </td>
                                                            <td
                                                            colSpan={2}
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'left',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {product_name}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {quantity}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {canViewCosts ? `$${unit_price}` : '—'}
                                                            </td>
                                                            <td
                                                            colSpan={2}
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                {canViewCosts ? `$${total}` : '—'}
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </Box>
                                </Grid>
                            </>
                            : ''}
                        {/* {invoice?.jobcost_service?.filter((item)=>item.product_id == null)?.length > 0 ? <Typography variant='h6' sx={{ mt: 2 }}>Additional Services</Typography> : ''} */}
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                <Box
                                    component="style"
                                    dangerouslySetInnerHTML={{
                                        __html: `
                                        .sales-order-table th {
                                            background-color: #d9d9d9 !important;
                                            color: black !important;
                                        }
                                        `
                                    }}
                                />
                                {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                <table
                                    className="sales-order-table"
                                    style={{
                                        width: '100%',
                                        border: '1px solid black',
                                        borderCollapse: 'collapse',
                                        textTransform: 'uppercase',
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: '4%' }} />
                                        <col style={{ width: '42%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '10%' }} />
                                    </colgroup>
                                    {invoice?.jobcost_service?.filter((item) => item.product_id == null)?.length > 0 ?
                                        <>
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        #
                                                    </th>
                                                    <th
                                                    colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    > ADDITIONAL SERVICES</th>
                                                    <th
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >QTY </th>
                                                    <th
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >RATE</th>
                                                    <th
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#d9d9d9',
                                                            fontWeight: 'bold',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >TOTAL COST</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoice?.jobcost_service?.filter((item) => item.product_id == null)?.map((ser, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >{index + 1}</td>
                                                            <td
                                                            colSpan={2}
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'left',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase',
                                                                    whiteSpace: 'pre-wrap'
                                                                }}
                                                            >
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                                    {ser?.service_notes ?
                                                                    <Stack direction={'column'}>
                                                                        {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                        <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000', whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                                    </Stack>
                                                                    : (ser?.service_name || ser?.name )}
                                                                </Typography>
                                                            </td>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >{ser ? parseFloat(ser?.quantity)?.toFixed(2) : 0}</td>
                                                            <td
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >{canViewCosts ? `$${ser ? parseFloat(ser?.service_original_cost)?.toFixed(2) : 0}` : '—'}</td>
                                                            {/* <TableCell>{ser ? parseFloat(ser?.jobcost_quantity)?.toFixed(2) : 0}</TableCell> */}
                                                            <td
                                                            colSpan={2}
                                                                style={{
                                                                    border: '1px solid black',
                                                                    padding: '12px',
                                                                    textAlign: 'center',
                                                                    backgroundColor: 'white',
                                                                    color: 'black',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >{canViewCosts ? `$${ser ? ser?.total_service_cost : 0}` : '—'}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </> : ''}
                                </table>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                <Box
                                    component="style"
                                    dangerouslySetInnerHTML={{
                                        __html: `
                .sales-order-table th {
                  background-color: #d9d9d9 !important;
                  color: black !important;
                }
              `
                                    }}
                                />
                                {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                <table
                                    className="sales-order-table"
                                    style={{
                                        width: '100%',
                                        border: '1px solid black',
                                        borderCollapse: 'collapse',
                                        textTransform: 'uppercase',
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: '4%' }} />
                                        <col style={{ width: '42%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '10%' }} />
                                    </colgroup>
                                    <tbody>
                                        {canViewCosts && (
                                        <>
                                        {(invoice?.jobcostTotal && invoice?.jobcostTotal > 0) ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>MATERIAL</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>${(invoice?.jobcostTotal).toFixed(2)}</td>
                                            </tr>
                                        ) : null}

                                        {(invoice?.project_category == 12) ? null : (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>PRODUCT</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    {/* ${invoice?.cust_product?.reduce((sum, val) => {
                                                        const {
                                                            product_retail_price = 0, material = 0, adjustment = 0, markAdjType,
                                                            product_total = 0, actual_retail_price, locationQuantity = 1, quantity = 1,
                                                            services = []
                                                        } = val;

                                                        const labourService = services?.find(s => s.labour_cost_service_id === 10);
                                                        const labourTotal = (labourService?.service_original_cost || 0) * (labourService?.prQty || 0) * locationQuantity;
                                                        const basePrice = actual_retail_price !== 0 && actual_retail_price != null
                                                            ? actual_retail_price
                                                            : product_retail_price;
                                                        let SPMA = (markAdjType === 2 || markAdjType === 3)
                                                            ? basePrice + Number(adjustment)
                                                            : basePrice + ((basePrice * Number(adjustment)) / 100);

                                                        const effectiveTotal = markAdjType === 1 && material === 0
                                                            ? (SPMA * (locationQuantity * quantity)) :
                                                            material === 1 ? (SPMA * locationQuantity)
                                                                : (SPMA * (locationQuantity * quantity));

                                                        return sum + effectiveTotal;
                                                    }, 0)?.toFixed(2)} */}
                                                    ${invoicePageProductTotalFromRows.toFixed(2)}
                                                </td>
                                            </tr>
                                        )}

                                        {(labourInstallService?.length > 0 ||
                                            additionalSelectedServices?.some(x => x.labour_cost_service_id === 10 && (x.total_cost || 0) > 0) ||
                                            invoice?.cust_product?.some(val => parseFloat(val.install_cost || 0) > 0)
                                        ) ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>INSTALL</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${(
                                                        //   (invoice?.cust_product?.reduce((sum, val) => {
                                                        //     const installTotal = val?.install_qty == 0 ? 0 : parseFloat(val?.install_total || 0);
                                                        //     if (installTotal) return sum + installTotal;

                                                        //     const installCost = parseFloat(val?.install_cost || 0);
                                                        //     const installQty = parseFloat(val?.install_qty ?? 1);
                                                        //     if (installCost) return sum + installCost * installQty;

                                                        //     return sum;
                                                        // }, 0) || 0) +
                                                        (invoicePageInstallTotalFromRows || 0) +
                                                        (additionalSelectedServices?.reduce(
                                                            (s, x) => x.labour_cost_service_id === 10 ? s + (x.total_cost || 0) : s,
                                                            0
                                                        ) || 0)
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        ) : null}

                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '12px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>{details?.project_category !== 11  ? 'ADDITIONAL' : 'SERVICE'}</span><span style={{ whiteSpace: 'nowrap' }}>SERVICES</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${(
                                                    (invoiceSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (additionalSelectedServices?.reduce(
                                                        (s, x) => x.labour_cost_service_id === 10 ? s : s + (x.total_cost || 0),
                                                        0
                                                    ) || 0) +
                                                    (cartService?.reduce((s, x) => {
                                                        const sp = x.sellingPrice || x.unit_cost || 0;
                                                        const markup = x.originalMarkup || x.product_markup || 0;
                                                        const final = sp + (sp * markup / 100);
                                                        return s + ((x.qty || 0) * final);
                                                    }, 0) || 0)
                                                )?.toFixed(2)}
                                            </td>
                                        </tr>

                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '12px', lineHeight: '14px', }}>SUBTOTAL</td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${fUSPrice(
                                                    (Number(invoice?.project_category) === 12 ? (subTotal || 0) : (invoicePageCustProductSubtotalForServices || 0)) +
                                                    (invoiceSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (additionalSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (cartService?.reduce((s, x) => {
                                                        const sp = x.sellingPrice || x.unit_cost || 0;
                                                        const markup = x.originalMarkup || x.product_markup || 0;
                                                        const final = sp + (sp * markup / 100);
                                                        return s + ((x.qty || 0) * final);
                                                    }, 0) || 0)
                                                )}
                                            </td>
                                        </tr>

                                        {invoice?.extra_discount > 0 ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}>DISCOUNT</td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>${invoice ? invoice?.extra_discount?.toFixed(2) : 0.00}</td>
                                            </tr>
                                        ) : null}

                                        {invoice?.tax_amount > 0 ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>SALES</span><span style={{ whiteSpace: 'nowrap' }}>TAX</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>${invoice ? invoice?.tax_amount?.toFixed(2) : 0.00}</td>
                                            </tr>
                                        ) : null}

                                        {showRushFee ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                {renderRushFeeLabelCell()}
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${fUSPrice(rushFeeAmount)}
                                                </td>
                                            </tr>
                                        ) : null}

                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}>TOTAL</td>
                                             {/* <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>${fUSPrice(invoicePageFooterTotal)}</td> */}
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>${fUSPrice(invoicePageDisplayTotal)}</td>
                                        </tr>

                                        {invoice?.project_category == 13 ? null : (
                                            <>
                                                {(userType != 3 && details?.job_category_name != 'Time & Material' && details?.job_category_name != 'No Charge' && details?.billing_type) ? (
                                                    <tr>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>BILLING</span><span style={{ whiteSpace: 'nowrap' }}>TYPE</span></div></td>
                                                        <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                            {details?.billing_type ? `${details?.billing_type} %` : "Nil"}
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {(invoice?.total && details?.billing_type) ? (
                                                    <tr>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>MINIMUM</span><span style={{ whiteSpace: 'nowrap' }}>PAYABLE</span><span style={{ whiteSpace: 'nowrap' }}>AMOUNT</span></div></td>
                                                        <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                            ${(invoicePageDisplayTotal && details?.billing_type) ? parseFloat((invoicePageDisplayTotal * details?.billing_type) / 100).toFixed(2) : 0.00}
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {details?.totalPaidAmount > 0 ? (
                                                    <>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>AMOUNT</span><span style={{ whiteSpace: 'nowrap' }}>PAID</span></div></td>
                                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                ${details?.totalPaidAmount ? details?.totalPaidAmount?.toFixed(2) : 0.00}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>BALANCE</span><span style={{ whiteSpace: 'nowrap' }}>DUE</span></div></td>
                                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                ${invoice?.newBalanceDue ? parseFloat(invoice?.newBalanceDue <= 0 ? 0.00 : invoice?.newBalanceDue)?.toFixed(2) : 0.00}
                                                            </td>
                                                        </tr>
                                                    </>
                                                ) : null}
                                            </>
                                        )}
                                        </>
                                        )}
                                    </tbody>
                                </table>
                            </Box>
                        </Grid>
                        {(details?.allInstallationCompleted == 1 && !empty(invoice?.note_data)) ? (
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 2 }}>
                                <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 510 }}>
                                    <Box
                                        component="style"
                                        dangerouslySetInnerHTML={{
                                            __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                        }}
                                    />
                                    <table
                                        className="sales-order-table"
                                        style={{
                                            width: '100%',
                                            border: '1px solid black',
                                            borderCollapse: 'collapse',
                                            textTransform: 'uppercase',
                                            tableLayout: 'fixed'
                                        }}
                                    >
                                        <colgroup>
                                            <col style={{ width: '4%' }} />
                                            <col style={{ width: '40%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '8%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '10%' }} />
                                            <col style={{ width: '10%' }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    Installation Notes
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!invoice?.note_data?.map((val, index) => {
                                                const { id, authorized_by, note, timestamp } = val;
                                                return (
                                                    <tr key={id}>
                                                        <td
                                                            style={{
                                                                border: '1px solid black',
                                                                padding: '12px',
                                                                textAlign: 'left',
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                textTransform: 'uppercase',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'left',
                                                                justifyContent: 'left'
                                                            }}
                                                        >
                                                            {note}
                                                            <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                                Added By: {authorized_by}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                                {moment.unix(timestamp).format('MM-DD-YYYY hh:mm:ss A')}
                                                            </Typography>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </Box>
                            </Grid>
                        ) : ''}
                    </>
                    :
                    <>
                        {(invoice?.cust_product?.length > 0) ? <>
                            <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                    <Box
                                        component="style"
                                        dangerouslySetInnerHTML={{
                                            __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                        }}
                                    />
                                    {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                    <table
                                        className="sales-order-table"
                                        style={{
                                            width: '100%',
                                            border: '1px solid black',
                                            borderCollapse: 'collapse',
                                            textTransform: 'uppercase',
                                            tableLayout: 'fixed'
                                        }}
                                    >
                                        <colgroup>
                                            <col style={{ width: '4%' }} />
                                            <col style={{ width: '42%' }} />
                                            <col style={{ width: '14%' }} /> {/* SKU */}
                                            <col style={{ width: '8%' }} />  {/* UNIT (blank) */}
                                            <col style={{ width: '12%' }} />  {/* QTY */}
                                            <col style={{ width: '10%' }} /> {/* UNIT PRICE */}
                                            <col style={{ width: '10%' }} /> {/* TOTAL */}
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    #
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    PRODUCTS DESCRIPTION
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    SKU
                                                </th>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    QTY 
                                                </th>
                                                {canViewCosts && (
                                                <>
                                                <th
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    RATE
                                                </th>
                                                <th
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    TOTAL COST
                                                </th>
                                                </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupedInvoiceDisplayRows?.map((row, index) => {
                                                if (row?.__type === 'group') {
                                                    const representative = row?.representative || {}
                                                    const groupName = getCustomerOrderProductGroupDisplayName({
                                                        groupMeta: row?.groupMeta,
                                                        representative,
                                                        members: row?.members,
                                                        groupId: row?.groupId,
                                                    })
                                                    const members = row?.members || []
                                                    const groupDisplayName = groupName
                                                    const groupDisplayNote =
                                                        members.find(
                                                            (m) =>
                                                                m?.product_notes != null &&
                                                                String(m.product_notes).trim() !== '',
                                                        )?.product_notes || ''
                                                    const groupProductDisplayQty = Number(row?.groupProductDisplayQty ?? 1) || 1
                                                    const groupInstallDisplayQty = Number(row?.groupInstallDisplayQty ?? 1) || 1
                                                    const groupInstallSubtotal = Number(row?.groupInstallSubtotal ?? 0)
                                                    const groupHasInstall = groupInstallSubtotal > 0
                                                    const displayGroupTotal = groupHasInstall ? Number(row?.groupProductOnlyTotal ?? 0) : Number(row?.totalValue || 0)
                                                    const unitEach = Number(row?.adjustedGroupRetailEach ?? 0) || (groupProductDisplayQty > 0 ? displayGroupTotal / groupProductDisplayQty : 0)
                                                    const groupInstallUnitEach = Number(row?.adjustedGroupInstallRetailEach ?? 0) || (groupInstallDisplayQty > 0 ? groupInstallSubtotal / groupInstallDisplayQty : 0)
                                                    const groupInstallNotes = getGroupInstallNotesDisplay(members)

                                                    return (
                                                        <React.Fragment key={`group-${row?.groupId}`}>
                                                            <tr>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                    {index + 1}
                                                                </td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color:'inherit' }}>
                                                                        {groupDisplayNote || groupDisplayName}
                                                                    </Typography>
                                                                </td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>{groupProductDisplayQty}</td>
                                                                {canViewCosts && (
                                                                    <>
                                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                            ${unitEach.toFixed(2)}
                                                                        </td>
                                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                            ${displayGroupTotal.toFixed(2)}
                                                                        </td>
                                                                    </>
                                                                )}
                                                            </tr>
                                                            {groupHasInstall && showInstallPriceInPreview && (
                                                                <tr>
                                                                    <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                    <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                        <Typography sx={{ color: groupInstallNotes ? 'red' : 'inherit', whiteSpace: 'pre-wrap' }}>
                                                                            {groupInstallNotes || 'Install Price'}
                                                                        </Typography>
                                                                    </td>
                                                                    <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                    <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>{groupInstallDisplayQty}</td>
                                                                    {canViewCosts && (
                                                                        <>
                                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                                ${groupInstallUnitEach.toFixed(2)}
                                                                            </td>
                                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                                ${groupInstallSubtotal.toFixed(2)}
                                                                            </td>
                                                                        </>
                                                                    )}
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    )
                                                }
                                                const val = row;
                                                const invoiceProductName = getInvoiceProductDisplayName(val)
                                                const { vid, product_name, product_notes, material, rfp, material_notes, vpt_number, actual_retail_price, install_cost, install_qty, install_total, unit_qty, adjustment, product_retail_price, quantity, product_total, locationQuantity, markAdjType, adj_value_type, install_notes, services = [] } = val;
                                                const labourService = services?.find(s => s.labour_cost_service_id === 10);

                                                // const labourService = services?.find((service) => service.labour_cost_service_id === 10);
                                                let basePriceForUnit = Number(actual_retail_price) || Number(product_retail_price) || 0;
                                                let SPMA = basePriceForUnit;
                                                if (material == 1) {
                                                    SPMA = SPMA + (adj_value_type == 1 ? Number(adjustment) : -Number(adjustment));
                                                }
                                                SPMA = parseFloat(SPMA)?.toFixed(2)

                                                let effectiveUnitPrice = install_cost ? Number(actual_retail_price) : Number(SPMA);
                                                let effectiveTotal = install_cost ? (Number(actual_retail_price) * locationQuantity * quantity) : (material === 1 ? Number(SPMA) * locationQuantity : Number(SPMA) * locationQuantity * quantity);

                                                return (
                                                    <React.Fragment key={vid}>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                {index + 1}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                    {material === 1 ? (material_notes || invoiceProductName) : (product_notes || invoiceProductName)}
                                                                </Typography>
                                                                {material === 1 && rfp === 0 && details?.job_category_name != 'Time & Material' && <Typography variant="subtitle2">(Addl. Material)</Typography>}
                                                            </td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black', wordBreak: 'break-all' }}>{vpt_number}</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>{locationQuantity}</td>
                                                            {canViewCosts && (
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                    ${(Number(effectiveUnitPrice) || 0).toFixed(2)}
                                                                </td>
                                                            )}
                                                            {canViewCosts && (
                                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                    ${(Number(effectiveTotal) || 0).toFixed(2)}
                                                                </td>
                                                            )}
                                                        </tr>
                                                        {install_cost > 0 && showInstallPriceInPreview && (
                                                            <tr>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'left', backgroundColor: 'white', color: 'black', wordBreak: 'break-word' }}>
                                                                    <Typography sx={{ color: install_notes ? 'red' : 'inherit', whiteSpace: 'pre-wrap' }}>{install_notes || 'Install Price'}</Typography>
                                                                </td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>-</td>
                                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>{install_qty ?? 1}</td>
                                                                {canViewCosts && (
                                                                    <>
                                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>${Number(install_cost).toFixed(2)}</td>
                                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                            ${(Number(install_cost) * (install_qty ?? 1)).toFixed(2)}
                                                                        </td>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </Box>
                            </Grid>
                        </> : null}

                        {/* {(additionalSelectedServices?.length > 0 || invoiceSelectedServices?.length > 0 || cartService?.length > 0) && <Typography variant='h4' sx={{ mt: 2 }}>{(details?.job_category_name != 'Service only' && details?.job_category_name != 'Time & Material') ? "Additional Services" : 'Services'}</Typography>} */}
                        {/* {(additionalSelectedServices?.length > 0 || invoiceSelectedServices?.length > 0 || cartService?.length > 0) && <Typography variant='h4' sx={{ mt: 2 }}>{(details?.job_category_name != 'Service only') ? "Additional Services" : 'Services'}</Typography>} */}
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                <Box
                                    component="style"
                                    dangerouslySetInnerHTML={{
                                        __html: `
                    .sales-order-table th {
                      background-color: #d9d9d9 !important;
                      color: black !important;
                    }
                  `
                                    }}
                                />
                                {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                <table
                                    className="sales-order-table"
                                    style={{
                                        width: '100%',
                                        borderBottom: '1px solid black',
                                        borderLeft: '1px solid black',
                                        borderRight: '1px solid black',
                                        borderTop: '1px solid black',
                                        borderCollapse: 'collapse',
                                        textTransform: 'uppercase',
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: '4%' }} />
                                        <col style={{ width: '42%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '10%' }} />
                                    </colgroup>
                                    {(additionalSelectedServices?.length > 0 || invoiceSelectedServices?.length > 0 || cartService?.length > 0) &&
                                        <thead>
                                            <tr>
                                                <th
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    #
                                                </th>
                                                <th
                                                    colSpan={2}
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {(details?.job_category_name != 'Service only') ? "ADDITIONAL SERVICES" : 'SERVICES'}
                                                </th>
                                                {/* <th
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    QTY  
                                                </th> */}
                                                {/* <th
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    RATE 
                                                </th> */}
                                                {canViewCosts && (
                                                <th
                                                    colSpan={4}
                                                    style={{
                                                        borderBottom: '1px solid black',
                                                        borderLeft: '1px solid black',
                                                        borderRight: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: '#d9d9d9',
                                                        fontWeight: 'bold',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    TOTAL COST
                                                </th>
                                                )}
                                            </tr>
                                        </thead>}
                                    <tbody>
                                        {openInvoice && invoiceSelectedServices?.map((ser, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {index + 1}
                                                    </td>
                                                    <td 
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'left',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                            {ser?.service_notes ?
                                                            <Stack direction={'column'}>
                                                                {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000' ,whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                            </Stack>
                                                            : (ser?.service_name || ser?.name )}
                                                        </Typography>
                                                    </td>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}> {ser ? ser?.quantity?.toFixed(2) : 0}</td>
                                                    {canViewCosts && (
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}> ${ser ? ser?.unit_cost?.toFixed(2) : 0}</td>
                                                    )}
                                                    {canViewCosts && (
                                                    <td 
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>${ser ? ser?.total_cost?.toFixed(2) : 0}</td>
                                                    )}
                                                </tr>
                                            )
                                        }
                                        )}
                                        {openInvoice && additionalSelectedServices
                                            ?.filter(servItem => {
                                                return true;
                                            })
                                            ?.map((ser, index) => {
                                                console.log('additionalSelectedServices--', ser);
                                                return (
                                                    <tr key={index}>
                                                        <td style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {index + 1}
                                                        </td>
                                                        <td 
                                                        colSpan={2}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'left',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase',
                                                            whiteSpace: 'pre-wrap'
                                                        }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                                {ser?.service_notes ?
                                                                <Stack direction={'column'}>
                                                                    {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                    <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000' ,whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                                </Stack>
                                                                : (ser?.service_name || ser?.name )}
                                                            </Typography>
                                                        </td>
                                                        {/* <td style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}> q4{ser ? ser?.quantity?.toFixed(2) : 0}</td> */}
                                                        {/* <td style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}> ${ser ? ser?.unit_cost?.toFixed(2) : 0}</td> */}
                                                        {canViewCosts && (
                                                        <td 
                                                        colSpan={4}
                                                        style={{
                                                            border: '1px solid black',
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            backgroundColor: 'white',
                                                            color: 'black',
                                                            textTransform: 'uppercase'
                                                        }}>${ser ? ser?.total_cost?.toFixed(2) : 0}</td>
                                                        )}
                                                    </tr>
                                                )
                                            }
                                            )}
                                        {openInvoice && cartService?.map((ser, index) => {
                                            console.log('cartService--', ser);
                                            ser.sellingPrice = details?.project_category == 13 ? ser.unit_cost : ser.sellingPrice || ser.unit_cost
                                            ser.originalMarkup = details?.project_category == 13 ? 0 : ser.originalMarkup || ser.product_markup || ser.markup
                                            return (
                                                <tr>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {index + 1}
                                                    </td>
                                                    <td 
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'left',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                            {ser?.service_notes ?
                                                            <Stack direction={'column'}>
                                                                {/* <Typography variant='body2' sx={{ textDecoration: 'underline', mt: 1, color: '#000000' }}>Service Note : </Typography> */}
                                                                <Typography variant='body2' sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', color: '#000000' ,whiteSpace: 'pre-wrap'}}>{ser.service_notes}</Typography>
                                                            </Stack>
                                                            : (ser?.name || ser?.service_name)}
                                                        </Typography>
                                                    </td>
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}>{ser ? ser?.qty?.toFixed(2) : 0}</td>
                                                    {canViewCosts && (
                                                    <td style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}> ${((ser.sellingPrice + (ser.sellingPrice * ser.originalMarkup / 100)))?.toFixed(2)}</td>
                                                    )}
                                                    {canViewCosts && (
                                                    <td 
                                                    colSpan={2}
                                                    style={{
                                                        border: '1px solid black',
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: 'white',
                                                        color: 'black',
                                                        textTransform: 'uppercase'
                                                    }}> ${(ser.qty * (ser.sellingPrice + (ser.sellingPrice * ser.originalMarkup / 100)))?.toFixed(2)}</td>
                                                    )}
                                                </tr>
                                            )
                                        }
                                        )}
                                    </tbody>
                                </table>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ textTransform: 'uppercase', color: 'black', minWidth: 1030 }}>
                                <Box
                                    component="style"
                                    dangerouslySetInnerHTML={{
                                        __html: `
                  .sales-order-table th {
                    background-color: #d9d9d9 !important;
                    color: black !important;
                  }
                `
                                    }}
                                />
                                {/* PROJECT CONTACT NAME AND PHONE NUMBER */}
                                <table
                                    className="sales-order-table"
                                    style={{
                                        width: '100%',
                                        borderBottom: '1px solid black',
                                        borderLeft: '1px solid black',
                                        borderRight: '1px solid black',
                                        borderCollapse: 'collapse',
                                        textTransform: 'uppercase',
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: '4%' }} />
                                        <col style={{ width: '42%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '10%' }} />
                                    </colgroup>
                                    <tbody>
                                        {canViewCosts && (
                                        <>
                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            {/* {details?.project_category !== 11 && ( */}
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>PRODUCT</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                            {/* )} */}
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                            {/* ${invoice?.cust_product?.reduce((sum, val, index) => {
                                                    const {
                                                        product_retail_price = 0, material = 0, adjustment = 0, markAdjType,
                                                        product_total = 0, actual_retail_price, locationQuantity = 1, quantity = 1,adj_value_type,
                                                        services = []
                                                    } = val;

                                                    const labourService = services?.find(s => s.labour_cost_service_id === 10);
                                                    const labourTotal = (labourService?.service_original_cost || 0) * (labourService?.prQty || 0) * locationQuantity;
                                                    const basePrice = actual_retail_price !== 0 && actual_retail_price != null
                                                        ? actual_retail_price
                                                        : product_retail_price;
                                                    let SPMA = (markAdjType === 2 || markAdjType === 3) ? basePrice : basePrice;
                                                    if (material == 1) {
                                                        let newSPMA = Number(SPMA) + (adj_value_type == 1 ? Number(adjustment) : -Number(adjustment));
                                                        SPMA = newSPMA
                                                        // console.log('SPMA-material==1',SPMA);
                                                        // console.log('SPMA-material==1',newSPMA);
                                                        // console.log('SPMA-material==1',adj_value_type);
                                                        // console.log('SPMA-material==1',adjustment);
                                                    }
                                                    const effectiveTotal = markAdjType === 1 && material === 0
                                                        ? (SPMA * (locationQuantity * quantity)) :
                                                        material === 1 ? (SPMA * locationQuantity)
                                                            : (SPMA * (locationQuantity * quantity));

                                                    return sum + effectiveTotal;
                                                }, 0)?.toFixed(2)} */}
                                                ${invoicePageProductTotalFromRows.toFixed(2)}
                                            </td>
                                        </tr>

                                        {(labourInstallService?.length > 0 ||
                                            additionalSelectedServices?.some(x => x.labour_cost_service_id === 10 && (x.total_cost || 0) > 0) ||
                                            invoice?.cust_product?.some(val => parseFloat(val.install_cost || 0) > 0)
                                        ) ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>INSTALL</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${(
                                                        // (invoice?.cust_product?.reduce((sum, val) => {
                                                        //     const installTotal = val?.install_qty == 0 ? 0 : parseFloat(val?.install_total || 0);
                                                        //     if (installTotal) return sum + installTotal;

                                                        //     const installCost = parseFloat(val?.install_cost || 0);
                                                        //     const installQty = parseFloat(val?.install_qty ?? 1);
                                                        //     if (installCost) return sum + installCost * installQty;

                                                        //     return sum;
                                                        // }, 0) || 0) +
                                                        (invoicePageInstallTotalFromRows || 0) +
                                                        (additionalSelectedServices?.reduce(
                                                            (s, x) => x.labour_cost_service_id === 10 ? s + (x.total_cost || 0) : s,
                                                            0
                                                        ) || 0)
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        ) : null}

                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '12px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>{details?.project_category !== 11  ? 'ADDITIONAL' : ''}</span><span style={{ whiteSpace: 'nowrap' }}>SERVICES</span><span style={{ whiteSpace: 'nowrap' }}>TOTAL</span></div></td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${(
                                                    (invoiceSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (additionalSelectedServices?.reduce(
                                                        (s, x) => x.labour_cost_service_id === 10 ? s : s + (x.total_cost || 0),
                                                        0
                                                    ) || 0) +
                                                    (cartService?.reduce((s, x) => {
                                                        const sp = x.sellingPrice || x.unit_cost || 0;
                                                        const markup = x.originalMarkup || x.product_markup || 0;
                                                        const final = sp + (sp * markup / 100);
                                                        return s + ((x.qty || 0) * final);
                                                    }, 0) || 0)
                                                )?.toFixed(2)}
                                            </td>
                                        </tr>

                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '12px', lineHeight: '14px', }}>SUBTOTAL</td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                ${fUSPrice(
                                                    (Number(invoice?.project_category) === 12 ? (subTotal || 0) : (invoicePageCustProductSubtotalForServices || 0)) +
                                                    (invoiceSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (additionalSelectedServices?.reduce((s, x) => s + (x.total_cost || 0), 0) || 0) +
                                                    (cartService?.reduce((s, x) => {
                                                        const sp = x.sellingPrice || x.unit_cost || 0;
                                                        const markup = x.originalMarkup || x.product_markup || 0;
                                                        const final = sp + (sp * markup / 100);
                                                        return s + ((x.qty || 0) * final);
                                                    }, 0) || 0)
                                                )}
                                            </td>
                                        </tr>

                                        {invoice?.extra_discount > 0 ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}>DISCOUNT</td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${invoice ? invoice?.extra_discount?.toFixed(2) : 0.00}
                                                </td>
                                            </tr>
                                        ) : null}

                                        {invoice?.tax_amount > 0 ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>SALES</span><span style={{ whiteSpace: 'nowrap' }}>TAX</span></div></td>
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${invoice ? invoice?.tax_amount?.toFixed(2) : 0.00}
                                                </td>
                                            </tr>
                                        ) : null}

                                        {showRushFee ? (
                                            <tr>
                                                <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                {renderRushFeeLabelCell()}
                                                <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                    ${fUSPrice(rushFeeAmount)}
                                                </td>
                                            </tr>
                                        ) : null}

                                        <tr>
                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}>TOTAL</td>
                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                            {/*  ${fUSPrice(invoicePageFooterTotal)} */}
                                                ${fUSPrice(invoicePageDisplayTotal)}
                                            </td>
                                        </tr>

                                        {invoice?.project_category == 13 ? null : (
                                            <>
                                                {(userType != 3 && details?.job_category_name != 'Time & Material' && details?.job_category_name != 'No Charge' && details?.billing_type) ? (
                                                    <tr>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>BILLING</span><span style={{ whiteSpace: 'nowrap' }}>TYPE</span></div></td>
                                                        <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                            {details?.billing_type ? `${details?.billing_type} %` : "Nil"}
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {(invoice?.total && details?.billing_type) ? (
                                                    <tr>
                                                        <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                        <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>MINIMUM</span><span style={{ whiteSpace: 'nowrap' }}>PAYABLE</span><span style={{ whiteSpace: 'nowrap' }}>AMOUNT</span></div></td>
                                                        <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                            ${(invoice?.total && details?.billing_type) ? parseFloat((invoice?.total * details?.billing_type) / 100).toFixed(2) : 0.00}
                                                        </td>
                                                    </tr>
                                                ) : null}

                                                {details?.totalPaidAmount > 0 ? (
                                                    <>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>AMOUNT</span><span style={{ whiteSpace: 'nowrap' }}>PAID</span></div></td>
                                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                ${details?.totalPaidAmount ? details?.totalPaidAmount?.toFixed(2) : 0.00}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td colSpan={2} style={{ border: '1px solid black', padding: '12px', backgroundColor: 'white' }}>&nbsp;</td>
                                                            <td style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: '#d9d9d9', fontWeight: 'bold', color: 'black', whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', hyphens: 'none', fontSize: '13px', lineHeight: '14px' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}><span style={{ whiteSpace: 'nowrap' }}>BALANCE</span><span style={{ whiteSpace: 'nowrap' }}>DUE</span></div></td>
                                                            <td colSpan={3} style={{ border: '1px solid black', padding: '12px', textAlign: 'center', backgroundColor: 'white', color: 'black' }}>
                                                                ${details?.dueAmount ? parseFloat(details?.dueAmount <= 0 ? 0.00 : details?.dueAmount)?.toFixed(2) : 0.00}
                                                            </td>
                                                        </tr>
                                                    </>
                                                ) : null}
                                            </>
                                        )}
                                        </>
                                        )}
                                    </tbody>
                                </table>
                            </Box>
                        </Grid>
                    </>
                }
            </Grid>
            </Stack>
        </>
    )
})
export default SalesOrderInvoicePage;